import { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { useState } from "react";
import {
  connectorsByIdentifier,
  connectorTypeOf,
} from "../../shared/connectors";
import {
  gitAuthIdentifiers,
  supportsGitAuth,
  supportsManualGitWebhook,
} from "../../shared/connectors/gitAuthIdentifiers";
import { sharedMutators } from "../../shared/mutators";
import { GIT_SYNC_STALE_MS } from "../../shared/mutators/syncGit";
import { queries } from "../../shared/queries";
import { isSupplierAvailableFromGrants } from "../../shared/rbac/crossResourceAvailability";
import { useConnectorCreator } from "../hooks/useConnectorCreator";
import { useGroupMemberships } from "../hooks/useGroupMemberships";
import { useQuery } from "../hooks/useQuery";
import { formatRelativeTime } from "../lib/formatRelativeTime";
import { makeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { ConfirmModal } from "./ConfirmModal";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorSelect } from "./ConnectorSelect";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { IntegrationButton } from "./IntegrationButton";
import { Link } from "./Link";
import { SettingsCard as Card } from "./SettingsCard";
import { Text } from "./Text";
import { Toggle } from "./Toggle";

const Header = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 8px;
`;

const HeaderText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RemoteBox = styled("div")`
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const RemoteIcon = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  color: var(--text-primary);
`;

const RemoteInfo = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const RepoLink = styled("a")`
  display: block;
  min-width: 0;
  align-self: flex-start;
  max-width: 100%;
  color: var(--text-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 8px;
`;

const spin = keyframes`
  to {
    transform: rotate(-360deg);
  }
`;

const SyncButton = styled(Button)`
  &.syncing span {
    animation: ${spin} 2s linear infinite;
  }
`;

// One entry per product, keeping the first (default) auth variant; the
// connector modal's variant picker offers the product's other auth methods.
const gitProviders = gitAuthIdentifiers
  .map((identifier) => connectorsByIdentifier[identifier]!)
  .filter(
    (definition, index, all) =>
      index ===
      all.findIndex(
        (d) =>
          connectorTypeOf(d.identifier)?.identifier ===
          connectorTypeOf(definition.identifier)?.identifier
      )
  );

function repoNameFromUrl(remoteUrl: string): string {
  const path = new URL(remoteUrl).pathname
    .replace(/^\/+/, "")
    .replace(/\.git$/, "");
  return path || remoteUrl;
}

type Props = {
  spaceId: string;
  canManageSpace: boolean;
};

export function GitSyncForm({ spaceId, canManageSpace }: Props) {
  const z = useZero();
  const [gitSync] = useQuery(queries.loadSpaceGitRemote({ spaceId }));
  const [allApps] = useQuery(queries.connectedAppsIndex());
  const [spaceGrants] = useQuery(
    queries.roleGrantsByScope({ scopeId: spaceId })
  );
  const { userGroupIds: userGroupMemberships } = useGroupMemberships();
  const [formOpen, setFormOpen] = useState(false);
  const [repoUrlValue, setRepoUrlValue] = useState("");
  const [selectedAppId, setSelectedAppId] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState<
    false | "toggle" | "row"
  >(false);
  const createConnector = useConnectorCreator(setSelectedAppId, spaceId);

  // syncStartedAt is set by the syncGit mutation and cleared when the
  // server-side task settles, so every client sees the same in-progress state.
  const isSyncing =
    gitSync?.syncStartedAt != null &&
    Date.now() - gitSync.syncStartedAt < GIT_SYNC_STALE_MS;

  const hasRemote = Boolean(
    gitSync?.remoteUrl && gitSync?.appUsage?.connectedAppId
  );
  const remoteApp = gitSync?.appUsage?.connectedApp;
  const gitApps =
    spaceGrants.length === 0
      ? []
      : allApps.filter(
          (app) =>
            supportsGitAuth(app.identifier) &&
            isSupplierAvailableFromGrants(
              "space_uses_connected_apps",
              spaceGrants,
              app.roleGrantsAsScope,
              userGroupMemberships
            )
        );

  const handleConfigure = async () => {
    if (!repoUrlValue.trim() || !selectedAppId) return;
    await z.mutate(
      sharedMutators.configureGitSync({
        spaceId,
        remoteUrl: repoUrlValue.trim(),
        connectedAppId: selectedAppId,
      })
    ).client;
    setRepoUrlValue("");
    setSelectedAppId("");
    setFormOpen(false);
  };

  const handleDisconnect = async () => {
    const reopenForm = confirmDisconnect === "row";
    await z.mutate(sharedMutators.disconnectGitSync({ spaceId })).client;
    setConfirmDisconnect(false);
    setFormOpen(reopenForm);
  };

  const handleSync = async () => {
    await z.mutate(sharedMutators.syncGit({ spaceId, action: "sync" })).client;
  };

  return (
    <>
      <Card>
        <Header>
          <HeaderText>
            <Text variant="text-regular-md">Git sync</Text>
            <Text variant="text-small" color="var(--text-secondary)">
              Sync workflows to a git repository
            </Text>
          </HeaderText>
          {canManageSpace && (
            <Toggle
              isOn={hasRemote || formOpen}
              onToggle={() => {
                if (hasRemote) {
                  setConfirmDisconnect("toggle");
                } else if (formOpen) {
                  setFormOpen(false);
                  setRepoUrlValue("");
                  setSelectedAppId("");
                } else {
                  setFormOpen(true);
                }
              }}
            />
          )}
        </Header>

        {hasRemote ? (
          <>
            <RemoteBox>
              <RemoteIcon>
                {remoteApp ? (
                  <ConnectorLogo
                    template={{
                      name: remoteApp.name,
                      identifier: remoteApp.identifier,
                    }}
                    size={16}
                  />
                ) : (
                  <Icon name="git-branch" size={16} />
                )}
              </RemoteIcon>
              <RemoteInfo>
                <RepoLink
                  href={gitSync!.remoteUrl.replace(/\.git$/, "")}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={gitSync!.remoteUrl}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {repoNameFromUrl(gitSync!.remoteUrl)}
                  </Text>
                </RepoLink>
                <Text variant="text-small" color="var(--text-secondary)">
                  {remoteApp && (
                    <>
                      {"Via "}
                      <Link
                        to={makeUrl(routes.modal.connector, {
                          id: remoteApp.id,
                        })}
                        style={{
                          color: "inherit",
                          textDecoration: "underline",
                        }}
                      >
                        {remoteApp.name}
                      </Link>
                      {" · "}
                    </>
                  )}
                  {isSyncing
                    ? "Syncing…"
                    : gitSync!.lastSyncedAt
                      ? `Last synced ${formatRelativeTime(gitSync!.lastSyncedAt)}`
                      : "Not synced yet"}
                </Text>
              </RemoteInfo>
              {canManageSpace && (
                <>
                  <SyncButton
                    className={isSyncing ? "syncing" : undefined}
                    variant="secondary"
                    icon="refresh-ccw"
                    onClick={handleSync}
                    disabled={isSyncing}
                    aria-label="Sync"
                    title="Sync"
                  />
                  <Button
                    variant="secondary"
                    icon="circle-minus"
                    onClick={() => setConfirmDisconnect("row")}
                    aria-label="Disconnect"
                    title="Disconnect"
                  />
                </>
              )}
            </RemoteBox>
            {!isSyncing && gitSync!.lastError && (
              <Text
                variant="text-small"
                color="var(--red-600)"
                style={{ padding: "0 8px" }}
              >
                {gitSync!.lastError}
              </Text>
            )}
            {canManageSpace &&
              remoteApp &&
              supportsManualGitWebhook(remoteApp.identifier) && (
                <WebhookStatus
                  spaceId={spaceId}
                  connectorId={remoteApp.id}
                  gitSync={gitSync!}
                />
              )}
          </>
        ) : formOpen ? (
          gitApps.length > 0 ? (
            <>
              <Field>
                <Text variant="text-small-md">Connector</Text>
                <ConnectorSelect
                  apps={gitApps}
                  value={selectedAppId || null}
                  selectedName={
                    gitApps.find((a) => a.id === selectedAppId)?.name ?? null
                  }
                  onChange={(id) => setSelectedAppId(id ?? "")}
                  placeholder="Select a connection"
                  addNew={gitProviders.map((provider) => ({
                    label: provider.name,
                    subtitle: provider.authLabel,
                    template: {
                      name: provider.name,
                      identifier: provider.identifier,
                    },
                    onClick: () => createConnector(provider.identifier),
                  }))}
                />
              </Field>
              <Field>
                <Text variant="text-small-md">Repository URL</Text>
                <Input
                  placeholder="https://github.com/org/repo.git"
                  value={repoUrlValue}
                  onChange={(e) => setRepoUrlValue(e.target.value)}
                  style={{ width: "100%" }}
                />
              </Field>
              {selectedAppId && repoUrlValue.trim() && (
                <div style={{ padding: "0 8px" }}>
                  <Button variant="primary" onClick={handleConfigure}>
                    Connect
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Field>
              <Text variant="text-small" color="var(--text-secondary)">
                To set up git sync, first connect a git provider.
              </Text>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {gitProviders.map((provider) => (
                  <IntegrationButton
                    key={provider.identifier}
                    type="button"
                    logo={provider.logo}
                    text={`Connect ${provider.name}`}
                    onClick={() => createConnector(provider.identifier)}
                  />
                ))}
              </div>
            </Field>
          )
        ) : null}
      </Card>

      <ConfirmModal
        open={confirmDisconnect !== false}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={handleDisconnect}
        title="Disconnect git sync"
        description="This will remove the git sync configuration for this space. Workflows will not be deleted."
        confirmLabel="Disconnect"
        destructive
      />
    </>
  );
}

// 3B configures the GitLab webhook itself, so this only reports the outcome.
function WebhookStatus({
  spaceId,
  connectorId,
  gitSync,
}: {
  spaceId: string;
  connectorId: string;
  gitSync: {
    remoteUrl: string;
    webhookReceivedAt: number | null;
    webhookConfiguredAt: number | null;
    webhookError: string | null;
  };
}) {
  const z = useZero();
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await z.mutate(
        sharedMutators.configureGitSync({
          spaceId,
          remoteUrl: gitSync.remoteUrl,
          connectedAppId: connectorId,
        })
      ).client;
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Field>
      <Text variant="text-regular-md">Webhooks</Text>
      {gitSync.webhookReceivedAt != null ? (
        <Text variant="text-small" color="var(--text-secondary)">
          Connected. GitLab delivered a webhook{" "}
          {formatRelativeTime(gitSync.webhookReceivedAt)}.
        </Text>
      ) : gitSync.webhookError != null ? (
        <>
          <Text variant="text-small" color="var(--red-600)">
            Couldn’t set up the webhook automatically. {gitSync.webhookError}
          </Text>
          <div>
            <Button
              variant="secondary"
              onClick={handleRetry}
              disabled={retrying}
            >
              Try again
            </Button>
          </div>
        </>
      ) : gitSync.webhookConfiguredAt != null ? (
        <Text variant="text-small" color="var(--text-secondary)">
          Webhook configured. Waiting for the first delivery from GitLab — push
          to the repository to check the connection.
        </Text>
      ) : (
        <Text variant="text-small" color="var(--text-secondary)">
          Setting up the webhook so GitLab tells 3B about pushes and merge
          requests…
        </Text>
      )}
    </Field>
  );
}
